import React, { useState, useEffect } from 'react';
import { doc, updateDoc, getDoc, deleteDoc } from 'firebase/firestore';
import { db, auth } from './firebase';
import './MarkInactive.css';

function MarkInactive({ student, onClose }) {
  const [formData, setFormData] = useState({
    exitTerm: '',
    lessonsAttended: '',
    writeOffFees: false,
    reason: '',
    notes: ''
  });
  const [schoolData, setSchoolData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    loadSchoolData();
  }, []);

  useEffect(() => {
    if (formData.exitTerm && formData.lessonsAttended && schoolData) {
      calculateSummary();
    }
  }, [formData.exitTerm, formData.lessonsAttended, formData.writeOffFees, schoolData]);

  const loadSchoolData = async () => {
    try {
      if (student.schoolRef) {
        const schoolDoc = await getDoc(student.schoolRef);
        if (schoolDoc.exists()) {
          setSchoolData({ id: schoolDoc.id, ...schoolDoc.data() });
        }
      }
    } catch (error) {
      console.error('Error loading school data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const calculateSummary = () => {
    const term = formData.exitTerm;
    
    // Case 1: Student has yearlyTracking (from PendingEnrollments)
    if (student.yearlyTracking && student.yearlyTracking[term]) {
      const termStatus = student.yearlyTracking[term];
      
      // CRITICAL FIX: Use individual term's totalDue, not yearly total
      const termFee = termStatus.totalDue || termStatus.instalments?.totalDue || 0;
      const lessonsPerTerm = schoolData?.lessonsPerTerm || 10;
      const costPerLesson = termFee / lessonsPerTerm;
      
      const lessonsAttended = parseInt(formData.lessonsAttended) || 0;
      const proRataAmount = Math.round(costPerLesson * lessonsAttended);
      
      const currentOwing = termStatus.totalOwing || termStatus.instalments?.totalOwing || 0;
      const alreadyPaid = termStatus.totalPaid || termStatus.instalments?.totalPaid || 0;
      
      let keepOwing = 0;
      let writeOff = 0;
      
      if (formData.writeOffFees) {
        // Write off everything
        writeOff = currentOwing;
        keepOwing = 0;
      } else {
        // Adjust to pro-rata
        const newAmountDue = proRataAmount;
        const newOwing = Math.max(0, newAmountDue - alreadyPaid);
        
        keepOwing = newOwing;
        writeOff = Math.max(0, currentOwing - newOwing);
      }

      setSummary({
        termFee,
        costPerLesson,  // Keep as number, formatCurrency will handle formatting
        lessonsPerTerm,
        lessonsAttended,
        proRataAmount,
        currentOwing,
        alreadyPaid,
        keepOwing,
        writeOff
      });
    }
    // Case 2: Student has no yearlyTracking (from AddStudent)
    else if (student.feeStructure) {
      const termFee = student.feeStructure.standardFee || 0;
      const lessonsPerTerm = student.feeStructure.lessonsPerTerm || schoolData?.lessonsPerTerm || 10;
      const costPerLesson = termFee / lessonsPerTerm;
      
      const lessonsAttended = parseInt(formData.lessonsAttended) || 0;
      const proRataAmount = Math.round(costPerLesson * lessonsAttended);
      
      // Calculate owing based on termPayments
      const termPayment = student.termPayments?.[term] || 0;
      const adminPayment = term === 'term1' ? (student.termPayments?.admin || 0) : 0;
      const totalPaid = termPayment + adminPayment;
      
      // For term1, include admin fee
      const termFeeWithAdmin = term === 'term1' ? termFee + (student.adminFee || 0) : termFee;
      const currentOwing = Math.max(0, termFeeWithAdmin - totalPaid);
      
      let keepOwing = 0;
      let writeOff = 0;
      
      if (formData.writeOffFees) {
        // Write off everything
        writeOff = currentOwing;
        keepOwing = 0;
      } else {
        // Adjust to pro-rata
        const proRataWithAdmin = term === 'term1' ? proRataAmount + (student.adminFee || 0) : proRataAmount;
        const newOwing = Math.max(0, proRataWithAdmin - totalPaid);
        
        keepOwing = newOwing;
        writeOff = Math.max(0, currentOwing - newOwing);
      }

      setSummary({
        termFee: termFeeWithAdmin,
        costPerLesson,  // Keep as number, formatCurrency will handle formatting
        lessonsPerTerm,
        lessonsAttended,
        proRataAmount: term === 'term1' ? proRataAmount + (student.adminFee || 0) : proRataAmount,
        currentOwing,
        alreadyPaid: totalPaid,
        keepOwing,
        writeOff
      });
    }
    else {
      setSummary(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.reason.trim()) {
      alert('Please provide a reason for marking the student inactive');
      return;
    }

    if (!window.confirm(
      `Are you sure you want to mark ${student.studentName} as inactive?\n\n` +
      `Amount to write off: R${summary?.writeOff.toFixed(2) || 0}\n` +
      `Amount remaining owed: R${summary?.keepOwing.toFixed(2) || 0}\n\n` +
      `This action can be reversed by reactivating the student later.`
    )) {
      return;
    }

    setLoading(true);

    try {
      const user = auth.currentUser;
      const studentRef = doc(db, 'students', student.id);
      const term = formData.exitTerm;

      const deactivationInfo = {
        deactivatedDate: new Date(),
        deactivatedBy: user.email,
        exitTerm: term,
        lessonsAttended: summary.lessonsAttended,
        proRataAmount: summary.proRataAmount,
        writeOffAmount: summary.writeOff,
        reason: formData.reason.trim(),
        notes: formData.notes.trim() || null,
        owingAtDeactivation: student.totalOwing || summary.currentOwing,
        owingAfterDeactivation: summary.keepOwing
      };

      // Case 1: Student has yearlyTracking (from PendingEnrollments)
      if (student.yearlyTracking) {
        const updatedYearlyTracking = { ...student.yearlyTracking };
        
        if (updatedYearlyTracking[term]) {
          const termStatus = updatedYearlyTracking[term];
          const newTotalDue = summary.proRataAmount;
          
          // Handle both nested and direct structures
          if (termStatus.instalments) {
            // Nested structure
            const newOwing = Math.max(0, newTotalDue - (termStatus.instalments.totalPaid || 0));
            updatedYearlyTracking[term] = {
              ...termStatus,
              instalments: {
                ...termStatus.instalments,
                totalDue: formData.writeOffFees ? termStatus.instalments.totalPaid : newTotalDue,
                totalOwing: formData.writeOffFees ? 0 : newOwing,
              },
              proRataAdjustment: {
                originalFee: termStatus.instalments.termFee,
                adjustedFee: summary.proRataAmount,
                lessonsAttended: summary.lessonsAttended,
                reason: 'Student marked inactive mid-term'
              }
            };
          } else {
            // Direct structure
            const newOwing = Math.max(0, newTotalDue - (termStatus.totalPaid || 0));
            updatedYearlyTracking[term] = {
              ...termStatus,
              totalDue: formData.writeOffFees ? termStatus.totalPaid : newTotalDue,
              totalOwing: formData.writeOffFees ? 0 : newOwing,
              proRataAdjustment: {
                originalFee: termStatus.termFee,
                adjustedFee: summary.proRataAmount,
                lessonsAttended: summary.lessonsAttended,
                reason: 'Student marked inactive mid-term'
              }
            };
          }
        }

        // Calculate new total owing across all terms
        const newTotalOwing = Object.values(updatedYearlyTracking).reduce((sum, termData) => {
          const owing = termData.totalOwing || termData.instalments?.totalOwing || 0;
          return sum + owing;
        }, 0);

        await updateDoc(studentRef, {
          isActive: false,
          yearlyTracking: updatedYearlyTracking,
          totalOwing: newTotalOwing,
          balance: -newTotalOwing,
          deactivationInfo: deactivationInfo,
          updatedAt: new Date()
        });
      }
      // Case 2: Student has no yearlyTracking (from AddStudent)
      else {
        // Update totalOwing and balance based on summary
        await updateDoc(studentRef, {
          isActive: false,
          totalOwing: summary.keepOwing,
          balance: -summary.keepOwing,
          deactivationInfo: deactivationInfo,
          updatedAt: new Date()
        });
      }

      alert(`${student.studentName} has been marked as inactive.`);
      onClose();
    } catch (error) {
      console.error('Error marking student inactive:', error);
      alert('Error marking student inactive. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    // Convert to number, handling strings, null, undefined
    const numAmount = parseFloat(amount) || 0;
    return `R${numAmount.toFixed(2)}`;
  };

  const getCurrentTermOptions = () => {
    const options = [];
    
    // Case 1: Student has yearlyTracking (from PendingEnrollments or InvoiceTerm)
    if (student.yearlyTracking) {
      const tracking = student.yearlyTracking;
      
      for (let i = 1; i <= 4; i++) {
        const termKey = `term${i}`;
        const termData = tracking[termKey];
        
        // Check for status in both possible locations (direct or nested in instalments)
        const status = termData?.status || termData?.instalments?.status;
        
        if (termData && status && status !== 'not_enrolled') {
          options.push({
            value: termKey,
            label: `Term ${i}`,  // Clean label without status
            status: status
          });
        }
      }
    }
    // Case 2: Student has invoices field (from AddStudent - manual entry)
    else if (student.invoices) {
      // For students without yearlyTracking, use invoices to determine available terms
      for (let i = 1; i <= 4; i++) {
        const termKey = `term${i}`;
        
        // If invoice exists for this term (even if null), it's a valid term
        if (termKey in student.invoices) {
          // Determine status based on invoice state and payments
          let status = 'not_invoiced';
          if (student.invoices[termKey] !== null) {
            // Invoice exists - check if paid
            const termPayment = student.termPayments?.[termKey] || 0;
            status = termPayment > 0 ? 'partial' : 'outstanding';
          }
          
          options.push({
            value: termKey,
            label: `Term ${i}`,  // Clean label without status
            status: status
          });
        }
      }
    }
    // Case 3: Fallback - use termPayments if available
    else if (student.termPayments) {
      for (let i = 1; i <= 4; i++) {
        const termKey = `term${i}`;
        if (termKey in student.termPayments) {
          options.push({
            value: termKey,
            label: `Term ${i}`,  // Clean label without status
            status: 'not_invoiced'
          });
        }
      }
    }
    
    return options;
  };

  if (loading && !schoolData) {
    return <div className="loading">Loading...</div>;
  }

  const handleDeleteStudent = async () => {
  const tp = student.termPayments || {};
  const totalPaid = (tp.term1 || 0) + (tp.term2 || 0) +
                    (tp.term3 || 0) + (tp.term4 || 0) +
                    (tp.admin || 0);

  if (totalPaid > 0) {
    alert(
      `❌ Cannot delete ${student.studentName}.\n\n` +
      `This student has R${totalPaid.toFixed(2)} in payments recorded.\n\n` +
      `Once payments are recorded the record cannot be deleted.\n` +
      `Please use "Mark Inactive" instead.`
    );
    return;
  }

  const confirmed = window.confirm(
    `⚠️ WARNING: Delete ${student.studentName}?\n\n` +
    `This student has no payments on the system.\n\n` +
    `If you delete this student ALL their records will be\n` +
    `permanently removed from the database.\n\n` +
    `This CANNOT be undone!\n\n` +
    `Click OK to permanently delete.`
  );

  if (!confirmed) return;

  try {
    await deleteDoc(doc(db, 'students', student.id));
    alert(`✅ ${student.studentName} has been permanently deleted.`);
    onClose(true); // pass true to signal a deletion happened
  } catch (error) {
    console.error('Error deleting student:', error);
    alert('❌ Error deleting student: ' + error.message);
  }
};

  return (
    <div className="modal-overlay">
      <div className="mark-inactive-modal">
        <div className="modal-header">
          <h2>🚫 Mark Student Inactive</h2>
          <button onClick={onClose} className="close-btn" disabled={loading}>
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-content">
            <div className="student-info-card">
              <h3>{student.studentName}</h3>
              <p><strong>Account:</strong> {student.accountNumber}</p>
              <p><strong>School:</strong> {student.schoolName}</p>
              <p><strong>Current Total Owing:</strong> {formatCurrency(student.totalOwing)}</p>
            </div>

            <div className="form-group">
              <label>Which term are they exiting? *</label>
              <select
                name="exitTerm"
                value={formData.exitTerm}
                onChange={handleChange}
                required
                disabled={loading}
              >
                <option value="">Select term</option>
                {getCurrentTermOptions().map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {formData.exitTerm && (
              <>
                <div className="form-group">
                  <label>How many lessons did they attend in {formData.exitTerm.replace('term', 'Term ')}? *</label>
                  <input
                    type="number"
                    name="lessonsAttended"
                    value={formData.lessonsAttended}
                    onChange={handleChange}
                    required
                    min="0"
                    max={schoolData?.lessonsPerTerm || 10}
                    disabled={loading}
                  />
                  <small>School guarantees: {schoolData?.lessonsPerTerm || 10} lessons per term</small>
                </div>

                {summary && (
                  <div className="calculation-summary">
                    <h4>Pro-Rata Calculation</h4>
                    <div className="calculation-details">
                      <p><strong>Term Fee:</strong> {formatCurrency(summary.termFee)}</p>
                      <p><strong>Cost per Lesson:</strong> {formatCurrency(summary.costPerLesson)}</p>
                      <p><strong>Lessons Attended:</strong> {summary.lessonsAttended} of {summary.lessonsPerTerm}</p>
                      <p><strong>Pro-Rata Amount Due:</strong> {formatCurrency(summary.proRataAmount)}</p>
                      <p><strong>Already Paid:</strong> {formatCurrency(summary.alreadyPaid)}</p>
                    </div>
                  </div>
                )}

                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      name="writeOffFees"
                      checked={formData.writeOffFees}
                      onChange={handleChange}
                      disabled={loading}
                    />
                    <span>Write off all remaining fees for this student</span>
                  </label>
                  <small>
                    {formData.writeOffFees 
                      ? 'All outstanding fees will be written off. Student will owe R0.' 
                      : 'Fees will be adjusted to pro-rata amount based on lessons attended.'}
                  </small>
                </div>
              </>
            )}

            <div className="form-group">
              <label>Reason for marking inactive *</label>
              <select
                name="reason"
                value={formData.reason}
                onChange={handleChange}
                required
                disabled={loading}
              >
                <option value="">Select a reason</option>
                <option value="Left school">Left school</option>
                <option value="Financial difficulties">Financial difficulties</option>
                <option value="Moved away">Moved away</option>
                <option value="Parent request">Parent request</option>
                <option value="Non-payment">Non-payment</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div className="form-group">
              <label>Additional Notes (Optional)</label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows="3"
                placeholder="Add any additional information..."
                disabled={loading}
              />
            </div>

            {summary && (
              <div className="final-summary">
                <h4>Summary of Changes</h4>
                <div className="summary-grid">
                  <div className="summary-item writeoff">
                    <span className="summary-label">Amount to Write Off:</span>
                    <span className="summary-value">{formatCurrency(summary.writeOff)}</span>
                  </div>
                  <div className="summary-item keeping">
                    <span className="summary-label">Amount Still Owed:</span>
                    <span className="summary-value">{formatCurrency(summary.keepOwing)}</span>
                  </div>
                </div>
                <ul className="summary-points">
                  <li>{summary.writeOff > 0 ? `R${summary.writeOff.toFixed(2)} will be written off` : 'No fees will be written off'}</li>
                  <li>{summary.keepOwing > 0 ? 'Outstanding fees remain collectible' : 'All fees will be written off'}</li>
                  <li>{summary.keepOwing > 0 ? 'Student will still appear on Outstanding Fees Report' : 'Student will be removed from Outstanding Fees Report'}</li>
                </ul>
              </div>
            )}
          </div>

          <div className="modal-actions">
            <button type="button" onClick={onClose} className="btn-cancel" disabled={loading}>
              Cancel
            </button>
            {/* DELETE BUTTON */}
            <button
              type="button"
              onClick={handleDeleteStudent}
              disabled={loading}
              style={{
                background: '#7f0000',
                color: 'white',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                marginRight: 'auto',
              }}
            >
              🗑️ Delete Student
            </button>

            <button type="submit" className="btn-danger" disabled={loading || !formData.exitTerm}>
              {loading ? 'Processing...' : 'Mark Inactive & Apply Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default MarkInactive;